/********************************************************************************
 * Copyright (c) 2021 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const { CompoundRequestCommand } = require('./RequestCommands');
const { DeleteCellsCommand, InsertCellsCommand } = require('./InsertDeleteCommands');
const EmptyCommand = require('./EmptyCommand');
const MarkCellValuesCommand = require('./MarkCellValuesCommand');
const OrgMarkCellValuesCommand = require('../MarkCellValuesCommand');
const PasteCellsCompoundCommand = require('./PasteCellsCompoundCommand');
const SetCellsCommand = require('./SetCellsCommand');
const SetPropertiesCommand = require('./SetPropertiesCommand');
const UpdateNamedCellCommand = require('./UpdateNamedCellCommand');
const ZoomChartCommand = require('./ZoomChartCommand');
const CellRange = require('../../model/CellRange');
const { getColIndexFromRef, getRowIndexFromRef, mapToServerProperties } = require('../../model/utils');
const {
	enumerateRangesColumns,
	enumerateRangesRows,
	getCellRangesFromSelection,
	getCellsInRanges,
	getSheetFromItem,
	toCellsColsRows
} = require('../utils');
const SelectCellsCommand = require('./SelectCellsCommand');
const ExecuteFunctionCommand = require('./ExecuteFunctionCommand');
const { default: UpdateGraphItemsCommand } = require('./UpdateGraphItemsCommand');
const DeleteTreeItemCommand = require('./DeleteTreeItemCommand');

const getSheet = (ranges) => (ranges[0] ? ranges[0].sheet : undefined);

const mapCellLevel = (sheet, toLeft) => {
	const hasKey = (col, row) => sheet.getCellProperties(col, row, true).key;
	const getLevel = (col, row) => sheet.getCellProperties(col, row, true).level;
	const levelToLeft = (col, row) => Math.max(0, getLevel(col, row) - 1);
	const levelToRight = (col, row) => {
		const level = getLevel(col, row);
		const parentLevel = getLevel(col, row - 1);
		return level <= parentLevel ? level + 1 : level;
	};
	const mapLevel = toLeft ? levelToLeft : levelToRight;

	return ({ ref }) => {
		const row = getRowIndexFromRef(ref);
		const column = getColIndexFromRef(ref);
		return hasKey(column, row) ? mapLevel(column, row) : 0;
	};
};

const mapPropertiesCommand = (type) => (ranges, map) => {
	const sheet = getSheet(ranges);
	const refs = toCellsColsRows(ranges, sheet);
	const properties = mapToServerProperties(type, map);
	return new SetPropertiesCommand(sheet, refs, properties, ranges);
};
const mapDeleteCellsCommand = (range, type, graph) => new DeleteCellsCommand(range, type, graph);
const mapInsertCellsCommand = (range, type, graph) => new InsertCellsCommand(range, type, graph);
const mapDeleteCellContentCommand = (sheet, selref, action) => {
	// action: 'all', 'formats', 'values' (, 'formulas'??)
	const ranges = getCellRangesFromSelection(selref, sheet);
	const cells = getCellsInRanges(ranges, sheet)
		.filter(({ cell }) => cell && (cell.getValue() != null || cell.hasFormula()))
		.map(({ reference }) => ({ reference }));
	const refs = toCellsColsRows(ranges, sheet);
	if (action === 'all') {
		return new CompoundRequestCommand(sheet)
			.add(new SetCellsCommand(sheet, cells))
			.add(new SetPropertiesCommand(sheet, refs, null, ranges));
	}
	return action === 'values'
		? new SetCellsCommand(sheet, cells)
		: action === 'formats'
		? new SetPropertiesCommand(sheet, refs, null, ranges)
		: undefined;
};
const mapSetCellsCommand = (sheet, cellrefs /* , execute */) => {
	// map level properties if specified:
	cellrefs = cellrefs.map((cell) => {
		const { level, ...fields } = cell;
		return level > 0 ? { properties: { attributes: { level } }, ...fields } : { ...fields };
	});
	return new SetCellsCommand(sheet, cellrefs);
};
const mapSetCellLevelsCommand = (sheet, ranges, down) => {
	const refs = { ...toCellsColsRows(ranges, sheet) };
	const mapLevel = mapCellLevel(sheet, down);
	refs.cells.forEach((cell) => {
		const level = mapLevel(cell);
		cell.properties = { attributes: { level } };
	});
	// SSF: improve - add a default attribute level property to prevent clear
	return new SetPropertiesCommand(sheet, refs, { attributes: { level: 0 } }, ranges);
};
const mapSetHeaderSectionSizeCommand = (header, index, ranges, size, visible, isRows, oldSize) => {
	const sheet = getSheet(ranges);
	// fix ranges: each must be either row or column section range! NOTE: subtract 1 since CellRange adds 1
	const toSectionRange =
		header.getItemType() === 'rowheadernode'
			? (range) => range.set(0, range.getY1(), sheet.maxColumns - 1, range.getY2())
			: (range) => range.set(range.getX1(), 0, range.getX2(), sheet.maxRows - 1);
	const refs = toCellsColsRows(ranges.map(toSectionRange), sheet);
	// -1 means ignore and not remove, so:
	if (size === -1 || !visible) size = undefined;
	return new SetPropertiesCommand(sheet, refs, { attributes: { size, visible } }, ranges);
};
const mapSetHeaderSectionLevelCommand = (header, ranges, key) => {
	const sheet = getSheet(ranges);
	const props = { cols: [], rows: [] };
	const decreaseSectionLevel = (index) => header.getSectionLevel(index, 1) - 1;
	const increaseSectionLevel = (index) => header.getSectionLevel(index, 1) + 1;
	const updateColLevel = (updateLevel) => (index, col, range) => {
		const newLevel = range.isRowRange() ? -1 : updateLevel(index);
		if (newLevel >= 0) props.cols.push({ ref: { col }, properties: { attributes: { level: newLevel } } });
	};
	const updateRowLevel = (updateLevel) => (index, row, range) => {
		const newLevel = range.isColumnRange() ? -1 : updateLevel(index);
		if (newLevel >= 0) props.rows.push({ ref: { row }, properties: { attributes: { level: newLevel } } });
	};
	switch (key) {
		case 'ArrowLeft':
			enumerateRangesRows(ranges, sheet, updateRowLevel(decreaseSectionLevel));
			break;
		case 'ArrowRight':
			enumerateRangesRows(ranges, sheet, updateRowLevel(increaseSectionLevel));
			break;
		case 'ArrowUp':
			enumerateRangesColumns(ranges, sheet, updateColLevel(decreaseSectionLevel));
			break;
		case 'ArrowDown':
			enumerateRangesColumns(ranges, sheet, updateColLevel(increaseSectionLevel));
			break;
	}
	return props.cols.length || props.rows.length
		? new SetPropertiesCommand(sheet, props, { attributes: { level: 0 } }, ranges)
		: new EmptyCommand(sheet);
};
const mapSetHeaderSectionOutlineFlagCommand = (header, index, closed) => {
	const type = header.getItemType();
	const sheet = header.getSheet().model;
	const refs =
		type === 'rowheadernode'
			? { rows: [{ ref: { row: index + 1 } }] }
			: { cols: [{ ref: { col: CellRange.getColumnStringFromIndex(index + sheet.colOffset, sheet) } }] };
	return sheet ? new SetPropertiesCommand(sheet, refs, { attributes: { closed } }) : new EmptyCommand(sheet);
};
const mapSetCellDataCommand = (sheet, cellref, expression /* , execute */) => {
	const descr = { reference: cellref };
	if (expression) {
		// do not set value => it will define initial value on server => no evaluation => set e.g. =12+23 -> 0!!
		descr.value = expression.hasFormula() ? undefined : expression.getValue();
		descr.formula = expression.hasFormula() ? expression.getFormula() : undefined;
	}
	return new SetCellsCommand(sheet, [descr]);
};
const mapMarkCellValuesCommand = (map) => {
	const { items, markers, references } = OrgMarkCellValuesCommand.markerFromItemCellRefMap(map);
	const sheet = getSheetFromItem(items[0]);
	return new MarkCellValuesCommand(sheet, references, markers);
};
const mapCompoundCommand = (type) => {
	switch (type) {
		case 'general':
		case 'formats':
			return new CompoundRequestCommand();
		case 'zoom':
			return new ZoomChartCommand();
	}
	return undefined;
};
const mapExecuteFunctionCommand = (item, functionStr) =>
	new ExecuteFunctionCommand(getSheetFromItem(item), functionStr);
const mapPasteCellsCommand = (target, data, json, action, fill) =>
	new PasteCellsCompoundCommand(target, data, action, fill);
const mapSelectCellsCommand = (graph, sheet, selectedCells) => new SelectCellsCommand(sheet, graph, selectedCells);

const mapUpdateNamedCellCommand = (action) => (graph, namedCell, newName, newExpression) => {
	const name = namedCell.getName();
	const expr = newExpression || namedCell.getExpression();
	const value = expr ? expr.getValue() : namedCell.getValue();
	const formula = expr && expr.hasFormula() ? expr.getFormula() : undefined;
	return new UpdateNamedCellCommand({ action, name, newName, value, formula });
};

const mapDeleteTreeItemCommand = (model, level) => {
	const msgbox = model.getParent().getParent().getParent();
	const messageId = level === -1 ? level : model.getTreeItemAt(level).id;
	const messageBox = msgbox.getItemType() === 'outboxcontainer' ? 'outbox' : 'inbox';
	const sheet = messageBox === 'inbox' ? msgbox.getParent().streamSheet.model : undefined;
	return new DeleteTreeItemCommand(sheet, messageBox, messageId);
};
const MapCommands = {
	'command.CompoundCommand': mapCompoundCommand,
	'command.AddSheetNameCommand': mapUpdateNamedCellCommand('update'),
	'command.CellAttributesCommand': mapPropertiesCommand('attributes'),
	'command.DeleteCellContentCommand': mapDeleteCellContentCommand,
	'command.DeleteCellsCommand': mapDeleteCellsCommand,
	'command.DeleteSheetNameCommand': mapUpdateNamedCellCommand('delete'),
	'command.DeleteTreeItemCommand': mapDeleteTreeItemCommand,
	'command.ExecuteFunctionCommand': mapExecuteFunctionCommand,
	'command.FormatCellsCommand': mapPropertiesCommand('formats'),
	'command.InsertCellsCommand': mapInsertCellsCommand,
	'command.MarkCellValuesCommand': mapMarkCellValuesCommand,
	'command.PasteCellsFromClipboardCommand': mapPasteCellsCommand,
	'command.SelectCellsCommand': mapSelectCellsCommand,
	'command.SetCellsCommand': mapSetCellsCommand,
	'command.SetCellDataCommand': mapSetCellDataCommand,
	'command.SetCellLevelsCommand': mapSetCellLevelsCommand,
	'command.SetHeaderSectionSizeCommand': mapSetHeaderSectionSizeCommand,
	'command.SetHeaderSectionLevelCommand': mapSetHeaderSectionLevelCommand,
	'command.SetHeaderSectionOutlineFlagCommand': mapSetHeaderSectionOutlineFlagCommand,
	'command.SetSheetNameCommand': mapUpdateNamedCellCommand('update'),
	'command.TextFormatCellsCommand': mapPropertiesCommand('textFormats'),
	'command.ZoomChartCommand': () => new ZoomChartCommand()
};
const Registry = {
	'command.server.DeleteCellsCommand': DeleteCellsCommand,
	'command.server.InsertCellsCommand': InsertCellsCommand,
	'command.server.MarkCellValuesCommand': MarkCellValuesCommand,
	'command.server.PasteCellsCompoundCommand': PasteCellsCompoundCommand,
	'command.server.SetCellsCommand': SetCellsCommand,
	'command.server.SetPropertiesCommand': SetPropertiesCommand,
	'command.server.UpdateGraphItemsCommand': UpdateGraphItemsCommand,
	'command.server.UpdateNamedCellCommand': UpdateNamedCellCommand,
	'command.server.ZoomChartCommand': ZoomChartCommand
};

const createCommand = (name, args) => {
	const Cmd = Registry[name];
	if (Cmd) return new Cmd(...args);
	const mapper = MapCommands[name];
	return mapper ? mapper(...args) : undefined;
};

const ServerCommandFactory = (basefactory) =>
	class extends basefactory {
		static get SERVER_COMMANDS() {
			return Object.keys(Registry);
		}
		static createCommand(graph, data, viewer) {
			const cmd = data ? Registry[data.name] : undefined;
			return cmd
				? cmd.createFromObject(data, { graph, viewer, factory: this })
				: super.createCommand(graph, data, viewer);
		}
		static create(cmdname, ...args) {
			return createCommand(cmdname, args) || super.create(cmdname, ...args);
		}
	};

module.exports = ServerCommandFactory;
